import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { formatDateOnly } from '../../lib/dates';
import { readNotes, type NotesAnswer } from '../../lib/notes-fill';
import type { ServiceRequestType } from '../../lib/service-requests';

function shown(answer: NotesAnswer): string {
  if (typeof answer.value === 'boolean') return answer.value ? 'Yes' : 'No';
  if (typeof answer.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(answer.value)) return formatDateOnly(answer.value) ?? answer.value;
  return String(answer.value);
}

/** Paste an email or notes, check what was read, then fill only the questions that are still empty. */
export default function FillFromNotesDialog({
  open,
  onOpenChange,
  type,
  answered,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: ServiceRequestType;
  /** Items already answered; they are never overwritten. */
  answered: ReadonlySet<string>;
  onApply: (answers: NotesAnswer[]) => void;
}) {
  const [text, setText] = useState('');
  const [found, setFound] = useState<NotesAnswer[] | null>(null);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setText('');
      setFound(null);
      setSkipped(new Set());
    }
  }, [open]);

  const chosen = (found ?? []).filter((answer) => !skipped.has(answer.item));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Fill from notes</DialogTitle>
          <DialogDescription>
            Paste an email, call notes or meeting notes. Pulse picks out the answers it can find, and you check them
            before anything is filled in. Questions that already have an answer are left alone.
          </DialogDescription>
        </DialogHeader>

        {found === null ? (
          <div className="space-y-2">
            <Label htmlFor="notes-text">Notes</Label>
            <Textarea
              id="notes-text"
              autoFocus
              rows={10}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={'e.g.\nCustomer: University College London\nContact: Priya Shah, priya@ucl.ac.uk, 020 7679 2000\nContract signed. 10 machines across 3 sites, install by 12 Nov.'}
            />
            <p className="text-xs text-muted-foreground">
              Lines like "Customer: UCL" work best. It also spots emails, phone numbers, dates, counts like "10 machines", and
              phrases like "contract signed" or "trial". The notes stay in your browser.
            </p>
          </div>
        ) : found.length === 0 ? (
          <p className="rounded-lg bg-muted/60 px-3 py-4 text-sm text-muted-foreground">
            No answers found in those notes. Try putting each answer on its own line, like "Customer: UCL" or "Machines: 10".
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-sm">
              Found {found.length} {found.length === 1 ? 'answer' : 'answers'}. Untick any that are wrong.
            </p>
            <ul className="max-h-80 divide-y overflow-y-auto rounded-lg border">
              {found.map((answer) => {
                const id = `notes-answer-${answer.item}`;
                return (
                  <li key={answer.item} className="flex items-start gap-3 px-3 py-2">
                    <Checkbox
                      id={id}
                      className="mt-0.5"
                      checked={!skipped.has(answer.item)}
                      onCheckedChange={(checked) =>
                        setSkipped((current) => {
                          const next = new Set(current);
                          if (checked) next.delete(answer.item);
                          else next.add(answer.item);
                          return next;
                        })
                      }
                    />
                    <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer text-sm">
                      <span className="block text-xs text-muted-foreground">{answer.label}</span>
                      <span className="block font-medium">{shown(answer)}</span>
                      <span className="block truncate text-xs text-muted-foreground/80">From: "{answer.from}"</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <DialogFooter>
          {found === null ? (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" disabled={!text.trim()} onClick={() => setFound(readNotes(text, type, answered))}>
                Read notes
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => setFound(null)}>
                Back to notes
              </Button>
              <Button
                type="button"
                disabled={chosen.length === 0}
                onClick={() => {
                  onApply(chosen);
                  onOpenChange(false);
                }}
              >
                Fill {chosen.length} {chosen.length === 1 ? 'answer' : 'answers'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
